<template>
  <section>
    <form method="post" @submit.prevent="onSend" novalidate>
      <div class="modal-card">
        <header class="modal-card-head">
          <p class="modal-card-title is-centered">
            {{ formTitle }}
          </p>
        </header>

        <section class="modal-card-body">
          <form-wrapper :validator="$v.form">
            <form-group
              name="sort"
              :label="$t('attributes.sort')"
              :label-position="labelPosition"
              expanded
            >
              <b-select
                v-model="form.sort"
                name="sort"
                :placeholder="
                  $t('message.please_select') + $t('attributes.sort')
                "
                expanded
              >
                <option
                  v-for="option in sortOptions"
                  :key="option"
                  :value="option"
                >
                  {{ option }}
                </option>
              </b-select>
            </form-group>

            <form-group
              name="name"
              :label="$t('attributes.name')"
              :label-position="labelPosition"
            >
              <b-autocomplete
                ref="traveler_type_input"
                v-model="selectedTravelerTypeName"
                name="TravelerTypeName"
                field="name"
                :data="travelerTypesList"
                :loading="isLoadingTravelerTypes"
                :placeholder="
                  $t('message.please_input') +
                    $t('attributes.traveler_type') +
                    $t('attributes.name')
                "
                :open-on-focus="false"
                @typing="loadTravelerTypesList"
                @select="onSelectTravelerType"
              >
                <template #default="props">
                  <p>
                    {{ props.option.name }} (
                    <!-- eslint-disable-next-line-->
                    <span v-text="props.option.age_min"></span>
                    歲 -
                    <span v-text="props.option.age_max"></span>
                    歲，
                    <span v-text="bedTypeName(props.option.type)"></span>
                    )
                  </p>
                </template>
                <template #empty>
                  {{
                    form.product_code
                      ? isLoadingTravelerTypes
                        ? $t('attributes.searching')
                        : $t('attributes.not_found')
                      : $t('message.please_input') +
                        $t('attributes.traveler_type') +
                        $t('attributes.name')
                  }}
                </template>
              </b-autocomplete>
            </form-group>

            <form-group
              name="type"
              attribute="attributes.traveler_bed_type"
              :label="$t('attributes.traveler_bed_type')"
              :label-position="labelPosition"
              expanded
            >
              <b-select
                v-model="form.traveler_type.type"
                name="type"
                :placeholder="
                  $t('message.please_select') +
                    $t('attributes.traveler_bed_type')
                "
                :disabled="true"
                expanded
              >
                <option v-for="type in bed_types" :value="type" :key="type">
                  {{ bedTypeName(type) }}
                </option>
              </b-select>
            </form-group>

            <div class="columns mb-0">
              <div class="column">
                <form-group
                  name="age_min"
                  :label="$t('attributes.age_min')"
                  :label-position="labelPosition"
                >
                  <b-input
                    v-model="form.traveler_type.age_min"
                    name="age_min"
                    min="0"
                    :placeholder="
                      $t('message.please_input') + $t('attributes.age_min')
                    "
                    :disabled="true"
                    expanded
                  ></b-input>
                </form-group>
              </div>
              <div class="column">
                <form-group
                  name="age_max"
                  :label="$t('attributes.age_max')"
                  :label-position="labelPosition"
                >
                  <b-input
                    v-model="form.traveler_type.age_max"
                    name="age_max"
                    min="0"
                    :placeholder="
                      $t('message.please_input') + $t('attributes.age_max')
                    "
                    :disabled="true"
                    expanded
                  ></b-input>
                </form-group>
              </div>
            </div>

            <form-group
              name="price"
              :label="$t('attributes.price')"
              :label-position="labelPosition"
            >
              <b-input
                v-model="form.price"
                name="price"
                type="number"
                :placeholder="
                  $t('message.please_input') + $t('attributes.price')
                "
                expanded
              ></b-input>
            </form-group>

            <form-group
              name="display_flag"
              :label="$t('attributes.want_display')"
              :label-position="labelPosition"
              expanded
            >
              <b-select
                v-model="form.display_flag"
                name="display_flag"
                expanded
              >
                <option value="true">{{ $t('attributes.display') }}</option>
                <option value="false">{{ $t('attributes.hide') }}</option>
              </b-select>
            </form-group>
          </form-wrapper>
        </section>

        <footer class="modal-card-foot">
          <b-button class="is-fullwidth" @click="$parent.close()">
            {{ $t('attributes.close') }}
          </b-button>
          <b-button
            :loading="sending"
            type="is-primary"
            class="is-fullwidth"
            native-type="submit"
          >
            {{ isNew ? $t('attributes.add') : $t('attributes.update') }}
          </b-button>
        </footer>
      </div>
    </form>
  </section>
</template>

<script>
import { required } from 'vuelidate/lib/validators'
import debounce from 'lodash/debounce'
import { TRAVELER_TYPES_API } from '@/utils/apis'
import notifyService from '@/services/notifyService'

export default {
  name: 'TravelerTypeForm',

  props: {
    travelerType: {
      type: Object,
      required: true
    }
  },
  data() {
    const isNew = !this.travelerType.id
    const formTitle =
      (isNew ? this.$t('attributes.add') : this.$t('attributes.update')) +
      this.$t('attributes.traveler_type')

    return {
      isNew,
      formTitle,
      sortOptions: [...Array(10).keys()].map(i => i + 1),
      bed_types: ['Bed', 'NoBed', 'AddBed', 'Infant'],

      form: this.extractFormValues(this.travelerType),

      sending: false,

      travelerTypeData: [],
      isLoadingTravelerTypes: false,
      travelerTypesList: [],
      selectedTravelerTypeName: this.travelerType.traveler_type.name,

      labelPosition: 'on-border'
    }
  },
  validations: {
    form: {
      sort: { required },
      price: { required },
      display_flag: { required },
      traveler_type_id: { required },
      traveler_type: {}
    }
  },
  methods: {
    extractFormValues(data) {
      return {
        id: data.id,
        sort: data.sort,
        price: data.price,
        display_flag: data.display_flag,
        traveler_type_id: data.traveler_type_id,
        traveler_type: data.traveler_type
      }
    },
    async loadAsyncTravelerTypesList() {
      const api = `${TRAVELER_TYPES_API}/list`

      const params = {
        name: this.selectedTravelerTypeName
      }

      this.isLoadingTravelerTypes = true
      try {
        const res = await this.axios.get(api, { params })
        this.travelerTypesList = res.data.data
      } catch (e) {
        this.travelerTypesList = []

        notifyService.fetch_error('TravelerTypes', e)
      } finally {
        this.isLoadingTravelerTypes = false
      }
    },
    loadTravelerTypesList: debounce(function() {
      this.loadAsyncTravelerTypesList()
    }, 500),
    onSelectTravelerType(option) {
      this.form.traveler_type_id = option.id
      this.form.traveler_type = option
      this.form.name = option.name
      this.form.age_min = option.age_min
      this.form.age_max = option.age_max
      this.form.type = option.type
    },
    async onSend() {
      this.$v.$touch()

      if (this.$v.$invalid) {
        return
      }

      this.$emit('send', this.form)
    }
  }
}
</script>
